/**
 * The Cost tab's drill-down filter objects, one per population (moved out of
 * CostTab.tsx for CR-5454, which pushed it further past the 500-line limit).
 *
 * Each object is what one Cost card or chart hands its drill-down, so the list
 * a user opens counts the same rows as the figure they clicked.
 */
import type { TabProps } from './types'
import { costLineParams, getNext20WeeksRange, incomingWindowParams } from './costTabFilters'

/**
 * Build every Cost tab drill-down's filter object from the dashboard filters.
 *
 * @param filters - the dashboard filter state
 * @param orderType - CR-7751: the dashboard's order-type toggle, as the cards read it
 * @returns one filter object per Cost population
 */
export function buildCostDrillFilters(filters: TabProps['filters'], orderType?: TabProps['orderType'], now: Date = new Date()) {
  // Scope-only filters (who/where, no date window) — shared by the drilldowns
  // whose rows are not bounded by the dashboard's date range.
  const scopeDrilldownFilters = {
    ...(filters?.supplier && { supplier: filters.supplier }),
    ...(filters?.customer && { customer: filters.customer }),
    ...(filters?.site && { site: filters.site }),
    // CR-7229: carry the warehouse scope into the drill-downs so the detail
    // list matches the tile it was opened from.
    ...(filters?.warehouse && { warehouse: filters.warehouse }),
    ...(filters?.siteCountry && { site_country: filters.siteCountry }),
    ...(filters?.supplierGroup && { supplier_group: filters.supplierGroup }),
    ...(filters?.supplierCountry && { supplier_country: filters.supplierCountry }),
    // CR-7751: the PO-header filters the cards apply — every drill-down now
    // applies them with the cards' rule (purchasing.services.dashboard_po_filters).
    ...(orderType && orderType !== 'all' ? { order_type: orderType } : {}),
    ...(filters?.commodityGroup && { lines__commodity_group: filters.commodityGroup }),
    ...(filters?.commodityType && { lines__commodity_type: filters.commodityType }),
    ...(filters?.supplierCode && { supplier_code: filters.supplierCode }),
  }

  // Convert dashboard filters to drilldown API format
  const drilldownFilters = {
    ...(filters?.dateFrom && { date_from: filters.dateFrom }),
    ...(filters?.dateTo && { date_to: filters.dateTo }),
    ...scopeDrilldownFilters,
  }

  // Arrears drilldown: exclude date_from/date_to since the dashboard computes
  // arrears on PO-level dates but drilldown applies them to delivery_date,
  // causing a mismatch. The overdue dynamic filter (delivery_date < today) is sufficient.
  // CR-5454: Contract Status / Part Number / Contract Type reach every Cost
  // drill, exactly as they reach the cards; the incoming window reaches only
  // the drills of windowed figures — never arrears (overdue as of today).
  const costLine = costLineParams(filters)
  const incomingWindow = incomingWindowParams(filters)

  const arrearsDrilldownFilters = { ...scopeDrilldownFilters, ...costLine }

  // CR-5456: PPV drilldown drops the dashboard window for the same reason.
  // The PPV bars are bucketed by promise date (Future) or ODD (Historical,
  // CR-8180), which routinely sit
  // outside a window whose date_to is capped at the end of last month
  // (historical mode) — passing it through filtered every contracted line out
  // and the modal opened on "No records found" under a populated chart.
  // PPVChartPanels adds the clicked month (and view) instead.
  const ppvDrilldownFilters = { ...scopeDrilldownFilters, ...costLine }
  // CR-1421 decision 3 (Aaron, 15 Oct 2026): the Future PPV view counts lines
  // promised from this Monday to the period's end, as legacy -- so its drills
  // send exactly that as the incoming window (the same promise-date rule).
  const ppvFutureDrilldownFilters = {
    ...ppvDrilldownFilters,
    incoming_from: getNext20WeeksRange(now).dateFrom,
    ...(incomingWindow['incoming_to'] ? { incoming_to: incomingWindow['incoming_to'] } : {}),
  }

  // CR-5454: Total Incoming drops the dashboard window for the same reason as
  // Arrears and PPV above. The card counts every open PO line regardless of
  // when it lands — its own "Incoming by Month" chart plots months past the
  // window's end — but the drilldown applies date_from/date_to to
  // delivery_date. Passing the window through made the card read 28 PO Lines
  // while the modal it opened showed 16, which is the count mismatch this
  // ticket is about.
  //
  // CR-5454 rework: the card now follows the Cost tab's time period (Next 20
  // weeks by default) — sent as the incoming window, which the backend applies
  // with the card's own date rule, so card and list still reconcile.
  const incomingDrilldownFilters = { ...scopeDrilldownFilters, ...costLine, ...incomingWindow }
  const suppliersDrilldownFilters = { ...drilldownFilters, ...costLine, ...incomingWindow }

  return {
    scopeDrilldownFilters, drilldownFilters, costLine, incomingWindow,
    arrearsDrilldownFilters, ppvDrilldownFilters, ppvFutureDrilldownFilters, incomingDrilldownFilters,
    suppliersDrilldownFilters,
  }
}

/**
 * The part of a clicked month the Incoming by Month bar counts: the month cut to
 * the Cost period, sent as the incoming window (CR-8610's axis, kept by Aaron
 * 16 Oct 2026). Under Next 20 weeks the last bar is only the period's first days
 * of that month, so its list must be too.
 *
 * @param range - the clicked month, from {@link parseMonthToDateRange}
 * @param window - the period's `incoming_from` / `incoming_to`, either may be absent
 * @returns `incoming_from` / `incoming_to` for the drill
 */
export const monthWithinPeriod = (
  range: { date_from: string; date_to: string },
  window: Record<string, string>,
): { incoming_from: string; incoming_to: string } => {
  const from = window['incoming_from']
  const to = window['incoming_to']
  return {
    incoming_from: from && from > range.date_from ? from : range.date_from,
    incoming_to: to && to < range.date_to ? to : range.date_to,
  }
}

// Helper function to parse month string to date range
export const parseMonthToDateRange = (monthStr: string): { date_from: string; date_to: string } | null => {
  try {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    const parts = monthStr.trim().split(' ')
    const monthIndex = monthNames.indexOf(parts[0])
    if (monthIndex === -1) return null
    let year: number
    if (parts.length >= 2 && parts[1]) {
      year = parseInt(parts[1])
      if (year < 100) year += 2000
    } else {
      year = new Date().getFullYear()
    }
    // CR-4671: Format in local timezone — see dashboardUtils.ts for context.
    const startDate = new Date(year, monthIndex, 1)
    const endDate = new Date(year, monthIndex + 1, 0)
    const fmt = (d: Date): string =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    return {
      date_from: fmt(startDate),
      date_to: fmt(endDate),
    }
  } catch {
    return null
  }
}
